// Connexion par empreinte / visage (WebAuthn) côté navigateur.
// La biométrie reste sur le téléphone : le serveur ne reçoit qu'une preuve signée.
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable
} from '@simplewebauthn/browser';

const CLE_APPAREIL = 'biometrie_active';
const cleMembre = (id) => `biometrie_active_${id}`;
const cleRefus = (id) => `biometrie_refusee_${id}`;
const CLE_PLUS_TARD = 'biometrie_plus_tard';

// localStorage peut être indisponible (navigation privée) : on ne plante jamais pour ça.
const lire = (stockage, cle) => { try { return stockage.getItem(cle); } catch { return null; } };
const ecrire = (stockage, cle, valeur) => { try { stockage.setItem(cle, valeur); } catch { /* ignoré */ } };
const effacer = (stockage, cle) => { try { stockage.removeItem(cle); } catch { /* ignoré */ } };

/** Ce téléphone sait-il demander l'empreinte / le visage dans le navigateur ? */
export const biometrieDisponible = async () => {
  try {
    return browserSupportsWebAuthn() && (await platformAuthenticatorIsAvailable());
  } catch {
    return false;
  }
};

/** La biométrie a-t-elle déjà été activée sur ce téléphone (par n'importe quel membre) ? */
export const appareilActive = () => lire(localStorage, CLE_APPAREIL) === '1';
export const membreActiveSurAppareil = (membreId) => lire(localStorage, cleMembre(membreId)) === '1';

export const marquerActive = (membreId) => {
  ecrire(localStorage, CLE_APPAREIL, '1');
  ecrire(localStorage, cleMembre(membreId), '1');
};
export const marquerDesactive = (membreId) => {
  effacer(localStorage, cleMembre(membreId));
  effacer(localStorage, CLE_APPAREIL);
};

// Invitation à activer : « plus tard » vaut pour la session, « non merci » pour toujours.
export const invitationRefusee = (membreId) => lire(localStorage, cleRefus(membreId)) === '1';
export const refuserInvitation = (membreId) => ecrire(localStorage, cleRefus(membreId), '1');
export const reporterInvitation = () => ecrire(sessionStorage, CLE_PLUS_TARD, '1');
export const invitationReportee = () => lire(sessionStorage, CLE_PLUS_TARD) === '1';

/** Message clair en français pour une erreur du navigateur ou du serveur. */
export const messageErreurBiometrie = (err) => {
  const nom = err?.name || err?.cause?.name;
  if (nom === 'NotAllowedError' || nom === 'AbortError') {
    return 'Opération annulée. Vous pouvez réessayer ou utiliser votre mot de passe.';
  }
  if (nom === 'InvalidStateError') return 'Cet appareil est déjà activé.';
  if (nom === 'SecurityError') return 'La biométrie ne peut pas être utilisée sur cette adresse.';
  return err?.response?.data?.message || 'La vérification a échoué. Utilisez votre mot de passe.';
};

/** Active la biométrie sur ce téléphone (membre déjà connecté). `api` = instance axios authentifiée. */
export const activerSurCetAppareil = async (api, membreId) => {
  const { data } = await api.post('/auth/webauthn/register/options');
  const reponse = await startRegistration(data.data);
  await api.post('/auth/webauthn/register/verify', { response: reponse });
  marquerActive(membreId);
};

/** Connexion par biométrie. Renvoie { token, membre } comme la connexion par mot de passe. */
export const connexionParBiometrie = async (api) => {
  const { data } = await api.post('/auth/webauthn/login/options');
  const { challengeId, options } = data.data;
  const reponse = await startAuthentication(options);
  const resultat = await api.post('/auth/webauthn/login/verify', { challengeId, response: reponse });
  return resultat.data.data;
};
